import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Users, Briefcase, MessageSquare, Star, Download, Trash2, Search, ExternalLink, Eye, MailOpen, ChevronDown, Phone, Mail, Globe, Facebook, Instagram, MessageCircle, MapPin, MousePointerClick, HeartHandshake, Calendar, Building2, Lightbulb, Stethoscope, Church, Megaphone, LayoutGrid, KeyRound, Copy, X, ShieldCheck, Heart } from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api, { API, formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import OnboardTab from "@/pages/admin/OnboardTab";
import PlansTab from "@/pages/admin/PlansTab";
import ModeratorsTab from "@/pages/admin/ModeratorsTab";
import CampaignsTab from "@/pages/admin/CampaignsTab";

const CHART_COLORS = ["#E97A3A", "#1F4E47", "#5A9A90", "#F4B183", "#163d37", "#8BA6A0", "#FFB47A"];

const Tabs = ({ value, onChange, items }) => (
  <div className="flex gap-2 flex-wrap border-b border-gray-200 overflow-x-auto">
    {items.map((it) => (
      <button
        key={it.k}
        onClick={() => onChange(it.k)}
        className={`px-4 py-3 text-sm font-bold uppercase tracking-wider transition-colors -mb-px border-b-2 whitespace-nowrap ${
          value === it.k ? "text-orange border-orange" : "text-teal-soft border-transparent hover:text-teal"
        }`}
        data-testid={`admin-tab-${it.k}`}
      >
        {it.label}
      </button>
    ))}
  </div>
);

const SidebarNav = ({ value, onChange, items }) => (
  <aside className="lg:sticky lg:top-6 lg:self-start w-full lg:w-56 shrink-0" data-testid="admin-sidebar">
    {/* Mobile: horizontal chips */}
    <div className="flex lg:hidden gap-2 overflow-x-auto pb-2 -mx-1 px-1">
      {items.map((it) => (
        <button
          key={it.k}
          onClick={() => onChange(it.k)}
          className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
            value === it.k ? "bg-orange text-white shadow-md shadow-orange/30" : "bg-white text-teal-soft border border-gray-200"
          }`}
          data-testid={`admin-nav-${it.k}`}
        >
          {it.Icon && <it.Icon size={14} />} {it.label}
        </button>
      ))}
    </div>
    {/* Desktop: vertical sidebar */}
    <nav className="hidden lg:block bg-white rounded-2xl border border-gray-200 p-2 space-y-1">
      {items.map((it) => (
        <button
          key={it.k}
          onClick={() => onChange(it.k)}
          className={`w-full text-left inline-flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            value === it.k
              ? "bg-orange text-white shadow-md shadow-orange/25"
              : "text-teal-soft hover:bg-cream hover:text-teal-deep"
          }`}
          data-testid={`admin-nav-${it.k}`}
        >
          {it.Icon && <it.Icon size={16} />} <span>{it.label}</span>
          {it.badge != null && it.badge > 0 && (
            <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${value === it.k ? "bg-white/25 text-white" : "bg-orange/15 text-orange"}`}>
              {it.badge}
            </span>
          )}
        </button>
      ))}
    </nav>
  </aside>
);

const ContactRow = ({ icon: Icon, label, value, href }) => {
  if (!value) return (
    <div className="flex items-start gap-2 text-teal-soft/50">
      <Icon size={14} className="mt-0.5 shrink-0" />
      <div><div className="text-xs uppercase tracking-wider font-bold">{label}</div><div className="text-sm">—</div></div>
    </div>
  );
  const inner = (
    <>
      <Icon size={14} className="mt-0.5 shrink-0 text-teal" />
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider font-bold text-teal">{label}</div>
        <div className="text-sm text-teal-deep break-all">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" className="flex items-start gap-2 hover:text-orange transition-colors">
      {inner}
    </a>
  ) : (
    <div className="flex items-start gap-2">{inner}</div>
  );
};

const StatCard = ({ icon: Icon, label, value, color = "teal", tone = "solid" }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 hover:border-orange transition-colors" data-testid={`kpi-${label}`}>
    <div className="flex items-center gap-3">
      <span
        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          tone === "soft"
            ? color === "orange" ? "bg-orange/10 text-orange" : "bg-teal/10 text-teal"
            : color === "orange" ? "bg-orange text-white" : "bg-teal text-white"
        }`}
      >
        <Icon size={18} />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] text-teal-soft uppercase tracking-wider font-bold truncate">{label}</div>
        <div className="font-display text-2xl text-teal-deep leading-none mt-1">{value}</div>
      </div>
    </div>
  </div>
);

const VolBadge = ({ active, label }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
      active ? "bg-orange/15 text-orange" : "bg-gray-100 text-gray-400"
    }`}
    title={label}
  >
    <HeartHandshake size={12} /> {active ? "✓" : "—"}
  </span>
);

const AdminPanel = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [chartDays, setChartDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [ents, setEnts] = useState([]);
  const [clients, setClients] = useState([]);
  const [allUsers, setAllUsers] = useState({ items: [], total: 0 });
  const [entities, setEntities] = useState([]);
  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [apoyoStats, setApoyoStats] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profilesStats, setProfilesStats] = useState({ by_kind: {}, total: 0 });
  const [kindFilter, setKindFilter] = useState("all");
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [signups, setSignups] = useState(null);
  const [signupGran, setSignupGran] = useState("daily");

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (user && !["admin", "moderator"].includes(user.role)) { navigate("/"); }
  }, [user, navigate]);

  const loadStats = async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); }
    catch (e) { setError(formatApiError(e)); }
    try { const { data } = await api.get("/appointments/admin/stats"); setApoyoStats(data); }
    catch (e) { console.warn("RED Online stats unavailable:", formatApiError(e)); }
  };
  const loadCharts = async (days = chartDays) => {
    try { const { data } = await api.get("/admin/stats/charts", { params: { days } }); setCharts(data); }
    catch (e) { setError(formatApiError(e)); }
  };
  const loadSignups = async () => {
    try { const { data } = await api.get("/admin/stats/signups"); setSignups(data); }
    catch (e) { console.warn("signups stats failed", formatApiError(e)); }
  };
  const loadEnts = async (search = "") => {
    try { const { data } = await api.get("/admin/entrepreneurs", { params: { q: search || undefined } }); setEnts(data.items); }
    catch (e) { setError(formatApiError(e)); }
  };
  const loadClients = async (search = "") => {
    try { const { data } = await api.get("/admin/clients", { params: { q: search || undefined } }); setClients(data.items); }
    catch (e) { setError(formatApiError(e)); }
  };
  const loadUsers = async (search = "") => {
    try { const { data } = await api.get("/admin/users", { params: { q: search || undefined } }); setAllUsers(data); }
    catch (e) { setError(formatApiError(e)); }
  };
  const loadEntities = async (search = "") => {
    try { const { data } = await api.get("/admin/entities", { params: { q: search || undefined } }); setEntities(data.items); }
    catch (e) { setError(formatApiError(e)); }
  };
  const loadEvents = async () => {
    try { const { data } = await api.get("/events"); setEvents(data.items); }
    catch (e) { setError(formatApiError(e)); }
  };
  const loadMessages = async () => {
    try { const { data } = await api.get("/admin/messages"); setMessages(data.items); }
    catch (e) { setError(formatApiError(e)); }
  };
  const loadProfiles = async (search = "", kind = "all") => {
    try {
      const { data } = await api.get("/admin/profiles", { params: { q: search || undefined, kind } });
      setProfiles(data.items || []);
    } catch (e) { setError(formatApiError(e)); }
    try {
      const { data } = await api.get("/admin/profiles/stats");
      setProfilesStats(data || { by_kind: {}, total: 0 });
    } catch (e) { console.error("profiles stats failed", e); }
  };

  useEffect(() => {
    if (user && user.role === "moderator" && tab !== "onboard") setTab("onboard");
  }, [user, tab]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    if (tab === "stats") loadCharts(chartDays);
    // eslint-disable-next-line
  }, [chartDays]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    loadStats();
    if (tab === "stats") { loadCharts(chartDays); loadSignups(); }
    else if (tab === "profiles") loadProfiles(q, kindFilter);
    else if (tab === "entrepreneurs") loadEnts(q);
    else if (tab === "clients") loadClients(q);
    else if (tab === "users") loadUsers(q);
    else if (tab === "entities") loadEntities(q);
    else if (tab === "events") loadEvents();
    else if (tab === "messages") loadMessages();
    // eslint-disable-next-line
  }, [tab, user]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const handle = setTimeout(() => {
      if (tab === "profiles") loadProfiles(q, kindFilter);
      else if (tab === "entrepreneurs") loadEnts(q);
      else if (tab === "clients") loadClients(q);
      else if (tab === "users") loadUsers(q);
      else if (tab === "entities") loadEntities(q);
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line
  }, [q, kindFilter]);

  const toggleFeatured = async (e) => {
    try { await api.patch(`/admin/entrepreneurs/${e.id}`, { featured: !e.featured }); loadEnts(q); loadStats(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const deleteEnt = async (e) => {
    if (!window.confirm(t.admin.confirm)) return;
    try { await api.delete(`/admin/entrepreneurs/${e.id}`); loadEnts(q); loadStats(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const deleteClient = async (c) => {
    if (!window.confirm(t.admin.confirm)) return;
    try { await api.delete(`/admin/clients/${c.id}`); loadClients(q); loadStats(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const deleteEntity = async (en) => {
    if (!window.confirm(t.admin.confirm)) return;
    try { await api.delete(`/admin/entities/${en.id}`); loadEntities(q); loadStats(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const deleteEvent = async (ev) => {
    if (!window.confirm(t.admin.confirm)) return;
    try { await api.delete(`/events/${ev.id}`); loadEvents(); loadStats(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const markRead = async (m) => {
    try { await api.patch(`/admin/messages/${m.id}/read`); loadMessages(); loadStats(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const deleteMsg = async (m) => {
    if (!window.confirm(t.admin.confirm)) return;
    try { await api.delete(`/admin/messages/${m.id}`); loadMessages(); loadStats(); }
    catch (err) { setError(formatApiError(err)); }
  };
  const toggleFeaturedProfile = async (p) => {
    try { await api.patch(`/admin/profiles/${p.id}/featured`); loadProfiles(q, kindFilter); }
    catch (err) { setError(formatApiError(err)); }
  };
  const deleteProfile = async (p) => {
    if (!window.confirm(t.admin.confirm)) return;
    try { await api.delete(`/admin/profiles/${p.id}`); loadProfiles(q, kindFilter); loadStats(); }
    catch (err) { setError(formatApiError(err)); }
  };

  const exportCsv = (kind) => {
    const token = localStorage.getItem("red.token");
    const url = `${API}/admin/export/${kind}.csv?_=${Date.now()}`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${kind}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch((e) => setError(String(e)));
  };

  if (!user || !["admin", "moderator"].includes(user.role)) {
    return <div className="container-tight py-24 text-center text-teal-soft">…</div>;
  }
  const isModerator = user.role === "moderator";

  return (
    <section className="bg-cream min-h-screen py-8 md:py-10">
      <div className="container-tight max-w-[1400px]">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="eyebrow text-orange">{t.admin.title}</p>
            <h1 className="font-display text-3xl md:text-4xl text-teal-deep mt-1 leading-tight">{t.admin.title}</h1>
            <p className="text-teal-soft mt-1 text-sm">{t.admin.subtitle}</p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <SidebarNav
            value={tab}
            onChange={setTab}
            items={isModerator ? [
              { k: "onboard", label: t.admin.tabs.onboard, Icon: Users },
            ] : [
              { k: "stats", label: t.admin.tabs.stats, Icon: LayoutGrid },
              { k: "users", label: t.admin.tabs.users, Icon: Users, badge: allUsers.total || undefined },
              { k: "profiles", label: t.admin.tabs.profiles, Icon: Briefcase, badge: profilesStats?.total },
              { k: "onboard", label: t.admin.tabs.onboard, Icon: Users },
              { k: "moderators", label: t.admin.tabs.moderators, Icon: ShieldCheck },
              { k: "campaigns", label: "Campañas", Icon: Megaphone },
              { k: "plans", label: t.admin.tabs.plans, Icon: Star },
              { k: "entrepreneurs", label: t.admin.tabs.entrepreneurs, Icon: Briefcase },
              { k: "clients", label: t.admin.tabs.clients, Icon: Users },
              { k: "entities", label: t.admin.tabs.entities, Icon: Building2 },
              { k: "events", label: t.admin.tabs.events, Icon: Calendar },
              { k: "messages", label: t.admin.tabs.messages, Icon: MessageSquare, badge: stats?.unread_messages },
            ]}
          />

          <div className="flex-1 min-w-0 w-full">
            {error && <p className="text-red-600 text-sm mb-4" data-testid="admin-error">{error}</p>}

            {tab === "stats" && stats && (
              <div className="space-y-6">
                {/* Date range filter */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="chart-range-filter">
                  <span className="text-xs uppercase tracking-wider font-bold text-teal-soft mr-2">Rango:</span>
                  {[
                    { d: 7, label: "7 días" },
                    { d: 30, label: "30 días" },
                    { d: 90, label: "90 días" },
                  ].map((r) => (
                    <button
                      key={r.d}
                      onClick={() => setChartDays(r.d)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                        chartDays === r.d ? "bg-teal-deep text-white" : "bg-white text-teal-soft border border-gray-200 hover:border-orange"
                      }`}
                      data-testid={`chart-range-${r.d}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>

                {/* KPI grid — grouped by purpose */}
                <div>
                  <h3 className="text-xs uppercase tracking-wider font-bold text-teal-soft mb-3">Comunidad</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
                    <StatCard icon={Briefcase} label={t.admin.stats.entrepreneurs} value={stats.entrepreneurs} tone="soft" />
                    <StatCard icon={Users} label={t.admin.stats.clients} value={stats.clients} tone="soft" />
                    <StatCard icon={Building2} label={t.admin.stats.entities} value={stats.entities || 0} color="orange" tone="soft" />
                    <StatCard icon={HeartHandshake} label={t.admin.stats.volunteers} value={stats.volunteers || 0} color="orange" tone="soft" />
                  </div>
                </div>
                <div data-testid="admin-ras-block">
                  <h3 className="text-xs uppercase tracking-wider font-bold text-teal-soft mb-3">R.A.S · Red de Amor y Solidaridad</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-xl p-4 text-white flex items-center gap-4" style={{ background: "linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)" }} data-testid="kpi-ras-month">
                      <span className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><Heart size={22} /></span>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-white/90">Inscritos por R.A.S este mes</div>
                        <div className="font-display text-3xl leading-none mt-1" data-testid="ras-month-value">{stats.ras_month || 0}</div>
                      </div>
                    </div>
                    <div className="rounded-xl p-4 border border-rose-200 bg-rose-50 flex items-center gap-4" data-testid="kpi-ras-total">
                      <span className="w-12 h-12 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0"><Heart size={22} /></span>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-rose-500">Total histórico R.A.S</div>
                        <div className="font-display text-3xl text-rose-700 leading-none mt-1" data-testid="ras-total-value">{stats.ras_total || 0}</div>
                      </div>
                    </div>
                  </div>
                  {Array.isArray(stats.ras_monthly) && stats.ras_monthly.some((p) => p.count > 0) && (
                    <div className="mt-3 bg-white rounded-xl border border-rose-200 p-4" data-testid="ras-monthly-chart">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-rose-500 mb-2">Tendencia mensual · últimos 12 meses</div>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={stats.ras_monthly} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1d6dc" />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(m) => (m || "").slice(2)} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                          <Tooltip cursor={{ fill: "#fdf2f4" }} formatter={(v) => [v, "Inscritos R.A.S"]} />
                          <Bar dataKey="count" fill="#f43f5e" radius={[4, 4, 0, 0]} name="Inscritos R.A.S" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-wider font-bold text-teal-soft mb-3">Engagement</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard icon={Eye} label="Vistas" value={stats.total_views || 0} tone="soft" />
                    <StatCard icon={MousePointerClick} label="Clics" value={stats.total_contact_clicks || 0} color="orange" tone="soft" />
                    <StatCard icon={Calendar} label={t.admin.stats.events} value={stats.events || 0} tone="soft" />
                    <StatCard icon={Star} label={t.admin.stats.featured} value={stats.featured} color="orange" tone="soft" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-wider font-bold text-teal-soft mb-3">Mensajes</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard icon={MessageSquare} label={t.admin.stats.messages} value={stats.messages} tone="soft" />
                    <StatCard icon={MailOpen} label={t.admin.stats.unread} value={stats.unread_messages} color="orange" tone="soft" />
                  </div>
                </div>

            {/* RED Online statistics */}
            {apoyoStats && (
              <div className="mt-8" data-testid="admin-redapoyo-stats">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 rounded-full bg-orange/15 text-orange flex items-center justify-center">
                    <HeartHandshake size={18} />
                  </span>
                  <div>
                    <p className="eyebrow text-orange">{t.redApoyo.navLabel}</p>
                    <h3 className="font-display text-2xl text-teal-deep leading-tight">RED Online</h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard icon={HeartHandshake} label={t.redApoyo.adminTotalHours} value={apoyoStats.total_hours || 0} color="orange" />
                  <StatCard icon={Users} label={t.redApoyo.adminUsersHelped} value={apoyoStats.unique_users_helped || 0} />
                  <StatCard icon={Calendar} label={t.redApoyo.adminBookings} value={apoyoStats.booked_slots || 0} />
                  <StatCard icon={Eye} label={t.redApoyo.statusOpen} value={apoyoStats.open_slots || 0} />
                </div>
                {(apoyoStats.top_professionals || []).length > 0 && (
                  <div className="mt-5 bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="bg-cream px-5 py-3 text-xs uppercase tracking-wider font-bold text-teal">
                      {t.redApoyo.adminTopPros}
                    </div>
                    <ul>
                      {apoyoStats.top_professionals.map((p) => (
                        <li key={p.professional_id} className="px-5 py-3 border-t border-gray-100 flex items-center gap-3" data-testid={`top-pro-${p.professional_id}`}>
                          <span className="w-8 h-8 rounded-full bg-orange/15 text-orange flex items-center justify-center shrink-0"><HeartHandshake size={13} /></span>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-teal-deep truncate">{p.professional_business || p.professional_name}</div>
                            <div className="text-xs text-teal-soft">{p.count} sesiones · {p.hours} h</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {charts && (
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 lg:col-span-2" data-testid="chart-signups">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-xl text-teal-deep">{t.admin.charts.signupsByDay}</h3>
                    <span className="text-xs text-teal-soft uppercase tracking-wider">{t.admin.charts.last30}</span>
                  </div>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={charts.signups_by_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="entrepreneurs" stroke="#E97A3A" strokeWidth={2} dot={false} name={t.admin.stats.entrepreneurs} />
                      <Line type="monotone" dataKey="clients" stroke="#1F4E47" strokeWidth={2} dot={false} name={t.admin.stats.clients} />
                      <Line type="monotone" dataKey="entities" stroke="#8B5CF6" strokeWidth={2} dot={false} name={t.admin.stats.entities} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="chart-pie">
                  <h3 className="font-display text-xl text-teal-deep mb-4">{t.admin.charts.byRole}</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={charts.by_role}
                        dataKey="count"
                        nameKey="role"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={(e) => `${e.role}: ${e.count}`}
                      >
                        {(charts.by_role || []).map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-6 lg:col-span-3" data-testid="chart-source">
                  <h3 className="font-display text-xl text-teal-deep mb-4">{t.admin.charts.bySource}</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.by_source}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="source" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#E97A3A" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Signups by profile type (daily/weekly/monthly) */}
            {signups && (
              <div className="mt-8" data-testid="admin-signups-block">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 rounded-full bg-teal/15 text-teal flex items-center justify-center">
                    <Users size={18} />
                  </span>
                  <div>
                    <p className="eyebrow text-orange">{t.admin.signups.title}</p>
                    <h3 className="font-display text-2xl text-teal-deep leading-tight">{t.admin.signups.subtitle}</h3>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="admin-signups-totals">
                  <StatCard icon={Users} label={t.admin.signups.professional} value={signups.totals.professional || 0} tone="soft" />
                  <StatCard icon={Briefcase} label={t.admin.signups.entrepreneur} value={signups.totals.entrepreneur || 0} color="orange" tone="soft" />
                  <StatCard icon={HeartHandshake} label={t.admin.signups.community} value={signups.totals.community || 0} tone="soft" />
                  <StatCard icon={Building2} label={t.admin.signups.entity} value={signups.totals.entity || 0} color="orange" tone="soft" />
                </div>

                {(() => {
                  const sumRow = (r) => (r ? (r.professional || 0) + (r.entrepreneur || 0) + (r.community || 0) + (r.entity || 0) : 0);
                  const today = (signups.daily || []).slice(-1)[0];
                  const week = (signups.weekly || []).slice(-1)[0];
                  const month = (signups.monthly || []).slice(-1)[0];
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="admin-signups-period">
                      <StatCard icon={Calendar} label="Hoy" value={sumRow(today)} color="orange" tone="soft" />
                      <StatCard icon={Calendar} label="Esta semana" value={sumRow(week)} tone="soft" />
                      <StatCard icon={Calendar} label="Este mes" value={sumRow(month)} tone="soft" />
                    </div>
                  );
                })()}

                {signups.share_token && (
                  <div className="mb-4 bg-cream/60 border border-teal/20 rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="admin-signups-share">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs uppercase tracking-wider font-bold text-orange mb-1">Enlace para tu jefe</div>
                      <div className="text-teal-soft text-xs md:text-sm break-all" data-testid="admin-signups-share-url">
                        {`${window.location.origin}/estadisticas?token=${signups.share_token}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        const url = `${window.location.origin}/estadisticas?token=${signups.share_token}`;
                        try {
                          await navigator.clipboard.writeText(url);
                          alert("Enlace copiado. Compártelo con quien quieras — solo verá estas estadísticas.");
                        } catch (err) {
                          console.error("clipboard failed", err);
                          window.prompt("Copia este enlace manualmente:", url);
                        }
                      }}
                      className="btn-teal-outline shrink-0"
                      data-testid="admin-signups-copy-link"
                    >
                      <Copy size={14} /> Copiar enlace
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { key: "daily", label: t.admin.signups.granDaily },
                    { key: "weekly", label: t.admin.signups.granWeekly },
                    { key: "monthly", label: t.admin.signups.granMonthly },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSignupGran(opt.key)}
                      data-testid={`signup-gran-${opt.key}`}
                      className={`text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 transition-colors ${
                        signupGran === opt.key
                          ? "bg-teal-deep text-white"
                          : "bg-teal/10 text-teal hover:bg-teal/20"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6" data-testid={`signup-chart-${signupGran}`}>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={signups[signupGran]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(d) => {
                          if (signupGran === "daily") return (d || "").slice(5);
                          if (signupGran === "weekly") return (d || "").replace(/^\d{4}-/, "");
                          return d;
                        }}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="professional" stackId="a" fill="#1F4E47" name={t.admin.signups.professional} />
                      <Bar dataKey="entrepreneur" stackId="a" fill="#E97A3A" name={t.admin.signups.entrepreneur} />
                      <Bar dataKey="community" stackId="a" fill="#8B5CF6" name={t.admin.signups.community} />
                      <Bar dataKey="entity" stackId="a" fill="#F59E0B" name={t.admin.signups.entity} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-4 bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid={`signup-table-${signupGran}`}>
                      <thead className="bg-cream border-b border-gray-200">
                        <tr className="text-left text-teal">
                          <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.signups.period}</th>
                          <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider text-right">{t.admin.signups.professional}</th>
                          <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider text-right">{t.admin.signups.entrepreneur}</th>
                          <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider text-right">{t.admin.signups.community}</th>
                          <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider text-right">{t.admin.signups.entity}</th>
                          <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider text-right">{t.admin.signups.total}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(signups[signupGran] || [])].reverse().map((row) => {
                          const rowTotal = (row.professional || 0) + (row.entrepreneur || 0) + (row.community || 0) + (row.entity || 0);
                          return (
                            <tr key={row.date} className="border-b border-gray-100 hover:bg-cream/40">
                              <td className="px-4 py-2 font-semibold text-teal-deep">{row.date}</td>
                              <td className="px-4 py-2 text-teal-soft text-right">{row.professional || 0}</td>
                              <td className="px-4 py-2 text-orange text-right">{row.entrepreneur || 0}</td>
                              <td className="px-4 py-2 text-teal-soft text-right">{row.community || 0}</td>
                              <td className="px-4 py-2 text-teal-soft text-right">{row.entity || 0}</td>
                              <td className="px-4 py-2 font-bold text-teal-deep text-right">{rowTotal}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
              </div>
            )}

        {(tab === "entrepreneurs" || tab === "clients" || tab === "entities" || tab === "users") && (
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[260px]">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-teal-soft" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.admin.search} className="field-input !pl-11 rounded-full" data-testid="admin-search" />
            </div>
            {tab !== "entities" && (
              <button onClick={() => exportCsv(tab === "entrepreneurs" ? "entrepreneurs" : tab === "users" ? "users" : "clients")} className="btn-teal-outline" data-testid="admin-export">
                <Download size={14} /> {t.admin.exportCsv}
              </button>
            )}
          </div>
        )}

        {tab === "users" && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-cream/60 border-b border-gray-200 text-sm text-teal-deep font-semibold" data-testid="admin-users-total">
              {allUsers.total} {t.admin.tabs.users.toLowerCase()}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead className="bg-cream border-b border-gray-200">
                  <tr className="text-left text-teal">
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.name}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.email}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.phone}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">Rol</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.city}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.date}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.source}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.items.length === 0 ? (
                    <tr><td className="px-4 py-8 text-center text-teal-soft" colSpan={8}>{t.admin.noResults}</td></tr>
                  ) : allUsers.items.map((u) => (
                    <tr key={u.id} className="border-b border-gray-100 hover:bg-cream/40" data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-4 py-3 font-semibold text-teal-deep">{u.full_name || "—"}</td>
                      <td className="px-4 py-3 text-teal-soft">{u.email || "—"}</td>
                      <td className="px-4 py-3 text-teal-soft">{u.phone || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${u.role === "admin" ? "bg-red-100 text-red-700" : u.role === "client" ? "bg-orange/10 text-orange" : "bg-teal/10 text-teal"}`}>
                          {t.admin.roles[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-teal-soft">{u.city}{u.state ? `, ${u.state}` : ""}</td>
                      <td className="px-4 py-3 text-teal-soft text-xs">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-teal-soft text-xs">
                        {u.source === "R.A.S" ? (
                          <span className="inline-flex items-center gap-1 bg-rose-100 text-rose-700 font-bold rounded-full px-2.5 py-1" data-testid={`admin-user-ras-${u.id}`} title="Red de Amor y Solidaridad">
                            <Heart size={11} /> R.A.S
                          </span>
                        ) : (u.source || "—")}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setResetTarget(u)}
                          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-orange/10 text-orange hover:bg-orange hover:text-white rounded-full px-3 py-1.5 transition-colors"
                          data-testid={`admin-reset-pw-${u.id}`}
                        >
                          <KeyRound size={11} /> Reset
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "onboard" && <OnboardTab />}
        {tab === "moderators" && <ModeratorsTab />}
        {tab === "campaigns" && <CampaignsTab />}
        {tab === "plans" && <PlansTab />}

        {tab === "profiles" && (
          <ProfilesTab
            profiles={profiles}
            stats={profilesStats}
            q={q}
            setQ={setQ}
            kindFilter={kindFilter}
            setKindFilter={setKindFilter}
            onToggleFeatured={toggleFeaturedProfile}
            onDelete={deleteProfile}
            t={t}
          />
        )}

        {tab === "entrepreneurs" && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-ents-table">
                <thead className="bg-cream border-b border-gray-200">
                  <tr className="text-left text-teal">
                    <th className="px-4 py-3 w-8"></th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.business}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.email}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.category}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.city}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.volunteer}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.source}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.featured}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {ents.length === 0 ? (
                    <tr><td className="px-4 py-8 text-center text-teal-soft" colSpan={9}>{t.admin.noResults}</td></tr>
                  ) : ents.map((e) => (
                    <React.Fragment key={e.id}>
                      <tr className="border-b border-gray-100 hover:bg-cream/40 cursor-pointer" onClick={() => setExpandedId(expandedId === e.id ? null : e.id)}>
                        <td className="px-4 py-3"><ChevronDown size={16} className={`text-teal-soft transition-transform ${expandedId === e.id ? "rotate-180" : ""}`} /></td>
                        <td className="px-4 py-3">
                          <div className="font-display text-lg text-teal-deep leading-tight">{e.business_name}</div>
                          <div className="text-xs text-teal-soft">{e.owner_name}</div>
                        </td>
                        <td className="px-4 py-3 text-teal-soft">{e.email}</td>
                        <td className="px-4 py-3 text-teal-soft">{t.categories[e.category] || e.category}</td>
                        <td className="px-4 py-3 text-teal-soft">{e.city}{e.state ? `, ${e.state}` : ""}</td>
                        <td className="px-4 py-3"><VolBadge active={e.volunteer} label={t.admin.volunteerBadge} /></td>
                        <td className="px-4 py-3 text-teal-soft text-xs">{e.source || "—"}</td>
                        <td className="px-4 py-3" onClick={(ev) => ev.stopPropagation()}>
                          <button onClick={() => toggleFeatured(e)} className={`${e.featured ? "text-orange" : "text-gray-300 hover:text-orange"} transition-colors`} data-testid={`feat-${e.id}`}>
                            <Star size={18} fill={e.featured ? "currentColor" : "none"} />
                          </button>
                        </td>
                        <td className="px-4 py-3" onClick={(ev) => ev.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <Link to={`/entrepreneur/${e.id}`} className="text-teal hover:text-orange" title={t.admin.view}><Eye size={16} /></Link>
                            <button onClick={() => deleteEnt(e)} className="text-red-500 hover:text-red-700" title={t.admin.delete} data-testid={`del-ent-${e.id}`}><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                      {expandedId === e.id && (
                        <tr className="bg-cream/60 border-b border-gray-100">
                          <td></td>
                          <td colSpan={8} className="px-4 py-5">
                            <div className="flex items-center gap-6 mb-4 pb-4 border-b border-gray-200">
                              <div className="flex items-center gap-2">
                                <span className="w-9 h-9 rounded-full bg-teal text-white flex items-center justify-center"><Eye size={16} /></span>
                                <div><div className="text-xs uppercase tracking-wider font-bold text-teal">Vistas al perfil</div><div className="font-display text-2xl text-teal-deep leading-none">{e.view_count || 0}</div></div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="w-9 h-9 rounded-full bg-orange text-white flex items-center justify-center"><MousePointerClick size={16} /></span>
                                <div><div className="text-xs uppercase tracking-wider font-bold text-teal">Clics de contacto</div><div className="font-display text-2xl text-teal-deep leading-none">{e.contact_click_count || 0}</div></div>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                              <ContactRow icon={Mail} label="Email" value={e.email} href={e.email ? `mailto:${e.email}` : ""} />
                              <ContactRow icon={Phone} label="Teléfono" value={e.phone} href={e.phone ? `tel:${e.phone}` : ""} />
                              <ContactRow icon={MessageCircle} label="WhatsApp" value={e.whatsapp} href={e.whatsapp ? `https://wa.me/${e.whatsapp.replace(/\D/g, "")}` : ""} />
                              <ContactRow icon={Globe} label="Sitio web" value={e.website} href={e.website || ""} />
                              <ContactRow icon={Facebook} label="Facebook" value={e.facebook} href={e.facebook ? (e.facebook.startsWith("http") ? e.facebook : `https://facebook.com/${e.facebook}`) : ""} />
                              <ContactRow icon={Instagram} label="Instagram" value={e.instagram} href={e.instagram ? (e.instagram.startsWith("http") ? e.instagram : `https://instagram.com/${e.instagram.replace("@", "")}`) : ""} />
                              <ContactRow icon={MapPin} label="Dirección" value={[e.address, e.city, e.state].filter(Boolean).join(", ") || "—"} />
                              <div className="md:col-span-2 lg:col-span-3">
                                <div className="text-xs uppercase tracking-wider text-teal font-bold mb-1">Descripción</div>
                                <p className="text-teal-soft text-sm leading-relaxed">{e.description || "—"}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "clients" && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-clients-table">
                <thead className="bg-cream border-b border-gray-200">
                  <tr className="text-left text-teal">
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.name}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.email}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.phone}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.city}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.volunteer}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.interests}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.source}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 ? (
                    <tr><td className="px-4 py-8 text-center text-teal-soft" colSpan={8}>{t.admin.noResults}</td></tr>
                  ) : clients.map((c) => (
                    <tr key={c.id} className="border-b border-gray-100 hover:bg-cream/40">
                      <td className="px-4 py-3 font-semibold text-teal-deep">{c.full_name}</td>
                      <td className="px-4 py-3 text-teal-soft">{c.email}</td>
                      <td className="px-4 py-3 text-teal-soft">{c.phone}</td>
                      <td className="px-4 py-3 text-teal-soft">{c.city}{c.state ? `, ${c.state}` : ""}</td>
                      <td className="px-4 py-3"><VolBadge active={c.volunteer} label={t.admin.volunteerBadge} /></td>
                      <td className="px-4 py-3 text-teal-soft text-xs">{(c.interests || []).map((k) => t.categories[k] || k).join(", ")}</td>
                      <td className="px-4 py-3 text-teal-soft text-xs">{c.source || "—"}</td>
                      <td className="px-4 py-3"><button onClick={() => deleteClient(c)} className="text-red-500 hover:text-red-700" title={t.admin.delete}><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "entities" && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-entities-table">
                <thead className="bg-cream border-b border-gray-200">
                  <tr className="text-left text-teal">
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.entity}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.entityType}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.representative}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.email}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.city}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {entities.length === 0 ? (
                    <tr><td className="px-4 py-8 text-center text-teal-soft" colSpan={6}>{t.admin.noResults}</td></tr>
                  ) : entities.map((en) => (
                    <tr key={en.id} className="border-b border-gray-100 hover:bg-cream/40">
                      <td className="px-4 py-3 font-semibold text-teal-deep">{en.entity_name}</td>
                      <td className="px-4 py-3 text-teal-soft">{t.entities.types[en.entity_type] || en.entity_type}</td>
                      <td className="px-4 py-3 text-teal-soft">{en.rep_name}</td>
                      <td className="px-4 py-3 text-teal-soft">{en.email}</td>
                      <td className="px-4 py-3 text-teal-soft">{en.city}{en.state ? `, ${en.state}` : ""}</td>
                      <td className="px-4 py-3"><button onClick={() => deleteEntity(en)} className="text-red-500 hover:text-red-700" data-testid={`del-entity-${en.id}`}><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "events" && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-events-table">
                <thead className="bg-cream border-b border-gray-200">
                  <tr className="text-left text-teal">
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.eventName}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.entity}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.when}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.location}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 ? (
                    <tr><td className="px-4 py-8 text-center text-teal-soft" colSpan={5}>{t.admin.noResults}</td></tr>
                  ) : events.map((ev) => (
                    <tr key={ev.id} className="border-b border-gray-100 hover:bg-cream/40">
                      <td className="px-4 py-3 font-semibold text-teal-deep">{ev.name}</td>
                      <td className="px-4 py-3 text-teal-soft">{ev.entity_name}</td>
                      <td className="px-4 py-3 text-teal-soft">{ev.date} {ev.time}</td>
                      <td className="px-4 py-3 text-teal-soft">{ev.location}</td>
                      <td className="px-4 py-3"><button onClick={() => deleteEvent(ev)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-messages-table">
                <thead className="bg-cream border-b border-gray-200">
                  <tr className="text-left text-teal">
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.name}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.email}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.message}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.date}</th>
                    <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.th.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.length === 0 ? (
                    <tr><td className="px-4 py-8 text-center text-teal-soft" colSpan={5}>{t.admin.noResults}</td></tr>
                  ) : messages.map((m) => (
                    <tr key={m.id} className={`border-b border-gray-100 ${m.read ? "" : "bg-orange/5"}`}>
                      <td className="px-4 py-3 font-semibold text-teal-deep">
                        {!m.read && <span className="inline-block w-2 h-2 rounded-full bg-orange mr-2" />}
                        {m.name}
                      </td>
                      <td className="px-4 py-3 text-teal-soft">{m.email}</td>
                      <td className="px-4 py-3 text-teal-soft max-w-md"><div className="line-clamp-2">{m.message}</div></td>
                      <td className="px-4 py-3 text-teal-soft text-xs">{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {!m.read && (<button onClick={() => markRead(m)} className="text-teal hover:text-orange" title={t.admin.contactReadAll}><MailOpen size={16} /></button>)}
                          <button onClick={() => deleteMsg(m)} className="text-red-500 hover:text-red-700" title={t.admin.delete}><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      <ResetPasswordModal
        target={resetTarget}
        onClose={() => { setResetTarget(null); setResetResult(null); }}
        onDone={(res) => { setResetResult(res); loadUsers(q); }}
        result={resetResult}
        t={t}
      />
    </section>
  );
};

const ResetPasswordModal = ({ target, onClose, onDone, result, t }) => {
  const [customPassword, setCustomPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    setCustomPassword("");
    setError("");
    setCopied(false);
  }, [target?.id]);

  if (!target) return null;

  const doReset = async () => {
    setLoading(true);
    setError("");
    try {
      const body = customPassword.trim() ? { password: customPassword.trim() } : {};
      const { data } = await api.post(`/admin/users/${target.id}/force-password-reset`, body);
      onDone?.(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!result?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(result.temporary_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("clipboard copy failed", err);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="admin-reset-modal">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <span className="w-10 h-10 rounded-full bg-orange/10 text-orange flex items-center justify-center">
            <KeyRound size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-orange">Restablecer contraseña</p>
            <h3 className="font-display text-xl text-teal-deep truncate">{target.full_name || target.email}</h3>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-cream hover:bg-orange hover:text-white text-teal-deep flex items-center justify-center" aria-label="Cerrar">
            <X size={16} />
          </button>
        </div>

        {result ? (
          <div className="p-5 space-y-4">
            <div className="rounded-2xl bg-cream border border-orange/30 p-4 text-center">
              <p className="text-xs uppercase tracking-wider text-teal-soft font-bold mb-2">Contraseña temporal</p>
              <div className="flex items-center gap-2 justify-center">
                <code className="font-mono text-2xl text-teal-deep bg-white rounded-lg px-4 py-2 border border-gray-200 tracking-widest select-all" data-testid="reset-pw-value">
                  {result.temporary_password}
                </code>
                <button type="button" onClick={copy} className="w-10 h-10 rounded-full bg-orange text-white hover:bg-orange/90 flex items-center justify-center" aria-label="Copiar" data-testid="reset-pw-copy">
                  <Copy size={14} />
                </button>
              </div>
              {copied && <p className="text-xs text-teal font-bold mt-2">¡Copiado!</p>}
            </div>
            <p className="text-sm text-teal-soft">
              Compártela con <strong className="text-teal-deep">{result.user.email || result.user.phone || result.user.full_name}</strong> por WhatsApp, correo o en persona. Pídele que inicie sesión con esta contraseña y la cambie desde su cuenta.
            </p>
            <button type="button" onClick={onClose} className="btn-orange w-full justify-center" data-testid="reset-pw-done">Listo</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <p className="text-sm text-teal-soft">
              Se generará una <strong className="text-teal-deep">contraseña temporal</strong> para este usuario y se mostrará solo esta vez. Cualquier enlace de recuperación pendiente quedará invalidado.
            </p>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-teal-soft block mb-1">
                O define una contraseña manualmente <span className="text-gray-400 font-normal">(opcional, mín 6)</span>
              </label>
              <input
                type="text"
                minLength={6}
                maxLength={64}
                value={customPassword}
                onChange={(e) => setCustomPassword(e.target.value)}
                placeholder="Dejar vacío = generar automática"
                className="field-input font-mono"
                data-testid="reset-pw-custom"
              />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="reset-pw-error">{error}</p>}
            <button
              type="button"
              onClick={doReset}
              disabled={loading || (customPassword && customPassword.trim().length < 6)}
              className="btn-orange w-full justify-center disabled:opacity-50"
              data-testid="reset-pw-submit"
            >
              <KeyRound size={14} /> {loading ? "Generando…" : "Generar contraseña temporal"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const KIND_META = {
  business: { Icon: Briefcase, color: "bg-orange" },
  venture: { Icon: Lightbulb, color: "bg-amber-500" },
  professional: { Icon: Stethoscope, color: "bg-teal" },
  ngo: { Icon: Building2, color: "bg-teal-deep" },
  church: { Icon: Church, color: "bg-violet-600" },
  community_leader: { Icon: Megaphone, color: "bg-rose-500" },
};

const ProfilesTab = ({ profiles, stats, q, setQ, kindFilter, setKindFilter, onToggleFeatured, onDelete, t }) => {
  const kinds = ["all", "business", "venture", "professional", "ngo", "church", "community_leader"];
  return (
    <div className="mt-6 space-y-5" data-testid="admin-profiles-tab">
      {/* Aggregate counts by kind */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-gray-200 flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-teal-deep text-white flex items-center justify-center"><LayoutGrid size={18} /></span>
          <div>
            <div className="text-xs uppercase tracking-wider text-teal-soft font-bold">{t.admin.profiles.total}</div>
            <div className="font-display text-2xl text-teal-deep leading-none mt-0.5" data-testid="profiles-stat-total">{stats.total || 0}</div>
          </div>
        </div>
        {kinds.slice(1).map((k) => {
          const M = KIND_META[k];
          return (
            <button
              key={k}
              onClick={() => setKindFilter(kindFilter === k ? "all" : k)}
              className={`bg-white rounded-2xl p-4 border text-left flex items-center gap-3 transition-all ${kindFilter === k ? "border-orange shadow-md" : "border-gray-200 hover:border-teal"}`}
              data-testid={`profiles-stat-${k}`}
            >
              <span className={`w-10 h-10 rounded-full ${M.color} text-white flex items-center justify-center`}><M.Icon size={18} /></span>
              <div>
                <div className="text-xs uppercase tracking-wider text-teal-soft font-bold">{t.myProfiles[`kind${k.charAt(0).toUpperCase() + k.slice(1).replace(/_(.)/g, (_, c) => c.toUpperCase())}`] || k}</div>
                <div className="font-display text-2xl text-teal-deep leading-none mt-0.5">{stats.by_kind?.[k] || 0}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-teal-soft" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.admin.profiles.searchPh} className="field-input pl-11 rounded-full" data-testid="admin-profiles-search" />
        </div>
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="field-input rounded-full max-w-[220px]" data-testid="admin-profiles-kind-filter">
          {kinds.map((k) => (
            <option key={k} value={k}>{k === "all" ? t.admin.profiles.kindAll : (t.myProfiles[`kind${k.charAt(0).toUpperCase() + k.slice(1).replace(/_(.)/g, (_, c) => c.toUpperCase())}`] || k)}</option>
          ))}
        </select>
      </div>

      {/* Profiles table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-profiles-table">
            <thead className="bg-cream border-b border-gray-200">
              <tr className="text-left text-teal">
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.profiles.thKind}</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.profiles.thName}</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.profiles.thOwner}</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.profiles.thCity}</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.admin.profiles.thCategory}</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider text-center">{t.admin.profiles.thFeatured}</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider text-right">{t.admin.profiles.thActions}</th>
              </tr>
            </thead>
            <tbody>
              {profiles.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-teal-soft">{t.admin.profiles.empty}</td></tr>
              )}
              {profiles.map((p) => {
                const M = KIND_META[p.kind] || KIND_META.business;
                const Icon = M.Icon;
                return (
                  <tr key={p.id} className="border-b border-gray-100 last:border-b-0 hover:bg-cream/50" data-testid={`admin-profile-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-white text-xs font-semibold ${M.color}`}>
                        <Icon size={11} /> {p.kind.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-teal-deep">{p.name}</div>
                      <div className="text-xs text-teal-soft">{p.description?.slice(0, 80)}{p.description?.length > 80 ? "…" : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-teal-soft">
                      <div>{p.owner_name || "—"}</div>
                      <div className="text-xs">{p.owner_email}</div>
                    </td>
                    <td className="px-4 py-3 text-teal-soft">{p.city || "—"}{p.state ? `, ${p.state}` : ""}</td>
                    <td className="px-4 py-3 text-teal-soft">{p.category || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => onToggleFeatured(p)}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${p.featured ? "bg-orange text-white" : "bg-gray-100 text-gray-400 hover:bg-orange/20"}`}
                        title={p.featured ? t.admin.profiles.unfeature : t.admin.profiles.feature}
                        data-testid={`featured-${p.id}`}
                      >
                        <Star size={14} fill={p.featured ? "currentColor" : "none"} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {p.legacy_entrepreneur_id && (
                          <a href={`/entrepreneur/${p.legacy_entrepreneur_id}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-cream text-teal hover:text-orange" title={t.admin.profiles.view} data-testid={`admin-profile-view-${p.id}`}>
                            <ExternalLink size={14} />
                          </a>
                        )}
                        <button
                          onClick={() => onDelete(p)}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                          title={t.admin.profiles.delete}
                          data-testid={`delete-profile-${p.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
